// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {forEach, filter, get, includes} from 'lodash';

// Components
import Button from '../components/core/button';
import FormConfirm from '../components/core/form-confirm';
import Loading from '../components/core/loading';

// Configs
import servicesFormConfig from '../../config/services-form-config';

// Containers
import withPermissions from './with-permissions';

// Redux Actions
import {deleteData, deleteDataReset} from '../actions/delete-data-actions';
import {refreshServicesData} from '../actions/services-data-actions';

// Contexts
import {RouteContext} from '../contexts/route-context';

// Styles
import './with-delete.scss';

const withDelete = (WrappedComponent, allowRoles, formName) => {
    class WithDelete extends Component {
        state = {
            deleteConfirm: false,
            deleteCompleted: false,
        };

        componentDidUpdate(nextProps) {
            const {searchValues} = this.props;

            if (searchValues.networkDisplayPathModifierId !== nextProps.searchValues.networkDisplayPathModifierId) {
                this.resetDeleteForm();
            }
        }

        getFormConfirmProps() {
            const {deleteCompleted} = this.state;
            const {error, response, searchValues} = this.props;

            return {
                completed: deleteCompleted,
                confirmMessage: 'Are you sure you want to delete this display path modifier?',
                confirmLabel: 'Delete',
                error,
                formSubmitReset: this.resetDeleteForm,
                onComplete: this.handleDeleteFormResetClick,
                onCancel: this.resetDeleteForm,
                onConfirm: this.handleDeleteButtonClick,
                response,
                searchData: searchValues,
                whitelistKeys: [
                    'networkDisplayPathModifierId',
                    'match',
                    'transformation',
                    'method',
                    'priority',
                    'description',
                    'network.name',
                ],
            };
        }

        handleDeleteButtonClick = () => {
            const {deleteData, refreshServicesData, refreshServicesDataKeys} = this.props;
            const {searchValues} = this.props;

            deleteData(
                searchValues.networkDisplayPathModifierId,
                servicesFormConfig.deleteDisplayPathModifier,
                'Successfully Deleted'
            ).then(() => {
                forEach(refreshServicesDataKeys, (value) => {
                    refreshServicesData(value);
                });
                this.setState({deleteConfirm: false, deleteCompleted: true});
            });
        };

        handleDeleteFormResetClick = () => {
            const {push} = this.context;

            this.resetDeleteForm();
            push('/display-path-modifiers');
        };

        handleDeleteShowFormClick = () => {
            this.setState({deleteConfirm: true, deleteCompleted: false});
        };

        resetDeleteForm = () => {
            const {deleteDataReset} = this.props;

            deleteDataReset();
            this.setState({deleteConfirm: false, deleteCompleted: false});
        };

        shouldRenderDeleteButton() {
            const {deleteCompleted, deleteConfirm} = this.state;
            const {fetching, hasAccessByRole, searchValues} = this.props;

            return (
                hasAccessByRole({allowRoles}) &&
                searchValues.networkDisplayPathModifierId &&
                !fetching &&
                !deleteConfirm &&
                !deleteCompleted
            );
        }

        shouldRenderFormConfirm() {
            const {deleteCompleted, deleteConfirm} = this.state;

            return deleteConfirm || deleteCompleted;
        }

        renderDeleteShowFormButton() {
            const props = {
                className: 'with-delete--button',
                onClick: this.handleDeleteShowFormClick,
            };

            return this.shouldRenderDeleteButton() ? (
                <div className="with-delete--buttons">
                    <Button {...props}>Delete</Button>
                </div>
            ) : null;
        }

        renderWrappedComponent = () => <WrappedComponent {...this.props} />;

        render() {
            const {fetching, searchFetching} = this.props;

            return (
                <div className="with-delete">
                    {this.renderDeleteShowFormButton()}
                    {this.shouldRenderFormConfirm() ? (
                        <FormConfirm {...this.getFormConfirmProps()} />
                    ) : (
                        this.renderWrappedComponent()
                    )}
                    <Loading loading={fetching || searchFetching} />
                </div>
            );
        }
    }

    WithDelete.propTypes = {
        deleteData: PropTypes.func.isRequired,
        deleteDataReset: PropTypes.func.isRequired,
        error: PropTypes.string,
        fetched: PropTypes.bool,
        fetching: PropTypes.bool,
        hasAccessByRole: PropTypes.func.isRequired,
        refreshServicesData: PropTypes.func.isRequired,
        refreshServicesDataKeys: PropTypes.array.isRequired,
        response: PropTypes.string,
        searchFetching: PropTypes.bool,
        searchValues: PropTypes.object,
    };

    WithDelete.displayName = `WithDelete(${getDisplayName(WrappedComponent)})`;

    WithDelete.contextType = RouteContext;

    const mapStateToProps = (state) => ({
        error: state.deleteData.error,
        fetching: state.deleteData.fetching,
        fetched: state.deleteData.fetched,
        response: state.deleteData.response,
        refreshServicesDataKeys: filter(
            get(servicesFormConfig, [formName, 'updateServiceDataKeys']),
            (value) => !includes(get(servicesFormConfig, [formName, 'providerServiceDataKey']), value)
        ),
        searchValues: get(
            get(state.servicesSearchData, [get(servicesFormConfig, [formName, 'providerServiceDataKey'])], {}),
            'searchData.content.0',
            {}
        ),
    });

    const mapDispatchToProps = {
        deleteData,
        deleteDataReset,
        refreshServicesData,
    };

    return withPermissions(connect(mapStateToProps, mapDispatchToProps)(WithDelete));
};

function getDisplayName(WrappedComponent) {
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default withDelete;
